import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ExternalLink, ThumbsUp, Eye, MessageSquare, Share2, Clock, Globe, RefreshCw, Lightbulb, TrendingUp, Target, Sparkles } from "lucide-react";
import { fetchDairyPosts, fetchDairyChart, fetchDairyStats, fetchDairySummary, refreshDairySummary, DAIRY_THEME_NAMES, DAIRY_THEME_COLORS } from "@/lib/api";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { toast } from "sonner";

const TABS = [
  { id: "all", label: "All", color: "#10B981" },
  { id: "reddit", label: "Reddit", color: "#FF4500" },
  { id: "tiktok", label: "TikTok", color: "#00F2EA" },
  { id: "news", label: "News Alerts", color: "#F59E0B" },
];

const PERIODS = [
  { value: '24h', label: '24H' },
  { value: '7d', label: '7 Days' },
  { value: '30d', label: '30 Days' },
];

const THEME_ENTRIES = [{ key: 'all', name: 'All Themes' }, ...Object.entries(DAIRY_THEME_NAMES).map(([key, name]) => ({ key, name }))];

export default function DairyPage() {
  const [platform, setPlatform] = useState("all");
  const [theme, setTheme] = useState("all");
  const [period, setPeriod] = useState("30d");
  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [chart, setChart] = useState([]);
  const [stats, setStats] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [summaryLoading, setSummaryLoading] = useState(false);

  const loadPosts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { platform, period, page, limit: 40 };
      if (theme !== 'all') params.theme = theme;
      const data = await fetchDairyPosts(params);
      setPosts(data.posts || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [platform, theme, period, page]);

  const loadChart = useCallback(async () => {
    try {
      const params = { period };
      if (theme !== 'all') params.theme = theme;
      const data = await fetchDairyChart(params);
      setChart(data.chart || []);
    } catch (e) { console.error(e); }
  }, [theme, period]);

  const loadStats = useCallback(async () => {
    try { setStats(await fetchDairyStats()); } catch (e) { console.error(e); }
  }, []);

  const loadSummary = useCallback(async () => {
    try {
      const data = await fetchDairySummary();
      setSummary(data.summary);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { loadStats(); loadSummary(); }, [loadStats, loadSummary]);
  useEffect(() => { setPage(1); }, [platform, theme, period]);
  useEffect(() => { loadPosts(); }, [loadPosts]);
  useEffect(() => { loadChart(); }, [loadChart]);

  const handleRefreshSummary = async () => {
    setSummaryLoading(true);
    try {
      await refreshDairySummary();
      toast.success("Summary refresh started. Check back in a minute.");
      setTimeout(loadSummary, 30000);
    } catch (e) { toast.error("Failed to refresh summary"); }
    finally { setSummaryLoading(false); }
  };

  const activePlatformColor = TABS.find(t => t.id === platform)?.color || '#10B981';

  return (
    <div className="space-y-6" data-testid="dairy-page">
      {/* AI Summary Section */}
      <DairySummaryCard summary={summary} onRefresh={handleRefreshSummary} loading={summaryLoading} />

      {/* Stats Row */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="dairy-stats">
          <StatCard label="Reddit Posts" value={stats.reddit_total} color="#FF4500" />
          <StatCard label="TikTok Videos" value={stats.tiktok_total} color="#00F2EA" />
          <StatCard label="News Alerts" value={stats.news_total || 0} color="#F59E0B" />
          <StatCard label="Last 24h" value={stats.recent_24h} color="#10B981" />
          <StatCard label="Sentiment" value={`${stats.sentiment?.positive || 0} pos / ${stats.sentiment?.negative || 0} neg`} color="#8B5CF6" small />
        </div>
      )}

      {/* Theme Stats */}
      {stats?.themes && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="dairy-theme-stats">
          {stats.themes.map(t => (
            <button key={t.theme} onClick={() => setTheme(t.theme === theme ? 'all' : t.theme)}
              className={`text-left transition-all duration-200 border ${theme === t.theme ? 'border-opacity-40' : 'border-gray-200 hover:border-gray-300'}`}
              style={theme === t.theme ? { borderColor: `${DAIRY_THEME_COLORS[t.theme]}60`, background: `${DAIRY_THEME_COLORS[t.theme]}08` } : {}}
              data-testid={`theme-stat-${t.theme}`}
            >
              <Card className="glass-card border-none rounded-none bg-transparent">
                <CardContent className="py-3 px-4">
                  <p className="font-display text-[10px] uppercase tracking-widest mb-1" style={{ color: DAIRY_THEME_COLORS[t.theme] }}>{t.name}</p>
                  <p className="font-mono-data text-lg font-bold text-gray-800">{t.count}</p>
                </CardContent>
              </Card>
            </button>
          ))}
        </div>
      )}

      {/* Platform Tabs + Period Filter */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-1 border-b border-gray-200 pb-0">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setPlatform(tab.id)}
              className={`px-6 py-3 font-display text-sm font-bold uppercase tracking-wider transition-all duration-200 border-b-2 ${
                platform === tab.id ? 'text-white border-current' : 'text-gray-400 border-transparent hover:text-gray-600'
              }`}
              style={platform === tab.id ? { color: tab.color, borderColor: tab.color } : {}}
              data-testid={`dairy-tab-${tab.id}`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="flex gap-1">
          {PERIODS.map(p => (
            <button
              key={p.value}
              onClick={() => setPeriod(p.value)}
              className={`px-4 py-2 text-xs font-display uppercase tracking-wider font-semibold transition-all duration-200 border ${
                period === p.value ? 'text-white border-current' : 'text-gray-400 border-gray-200 hover:text-gray-800'
              }`}
              style={period === p.value ? { color: activePlatformColor, borderColor: activePlatformColor, background: `${activePlatformColor}12` } : {}}
              data-testid={`dairy-period-${p.value}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Theme Filter */}
      <div className="flex flex-wrap gap-2" data-testid="dairy-theme-filters">
        {THEME_ENTRIES.map(({ key, name }) => (
          <button
            key={key}
            onClick={() => setTheme(key)}
            className={`px-3 py-1.5 text-xs font-display uppercase tracking-wider font-semibold transition-all duration-200 border flex items-center gap-2 ${
              theme === key ? "text-white" : "bg-transparent border-gray-200 text-gray-500 hover:text-gray-800"
            }`}
            style={theme === key ? {
              background: `${DAIRY_THEME_COLORS[key] || '#fff'}15`,
              borderColor: `${DAIRY_THEME_COLORS[key] || '#fff'}40`,
              color: key === 'all' ? '#fff' : DAIRY_THEME_COLORS[key]
            } : {}}
            data-testid={`dairy-theme-${key}`}
          >
            {key !== 'all' && <span className="w-2 h-2 rounded-sm" style={{ background: DAIRY_THEME_COLORS[key] }} />}
            {name}
          </button>
        ))}
      </div>

      {/* Mentions Volume Chart */}
      {chart.length > 0 && (
        <Card className="glass-card border-gray-200 rounded-none" data-testid="dairy-volume-chart">
          <CardHeader className="pb-2 border-b border-gray-200">
            <CardTitle className="font-display text-sm font-semibold tracking-wider uppercase text-gray-800">
              Dairy Mentions Volume
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4 pb-2">
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={chart} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="date" tick={{ fill: '#6B7280', fontSize: 10, fontFamily: 'Inter, Arial, sans-serif' }}
                  tickFormatter={d => { const dt = new Date(d); return `${dt.getDate()}/${dt.getMonth()+1}`; }} />
                <YAxis tick={{ fill: '#6B7280', fontSize: 10 }} />
                <Tooltip
                  contentStyle={{ background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: 0, fontFamily: 'Inter, Arial, sans-serif', fontSize: 11 }}
                  labelStyle={{ color: '#6B7280' }}
                />
                <Bar dataKey="reddit" fill="#FF4500" opacity={0.8} />
                <Bar dataKey="tiktok" fill="#00F2EA" opacity={0.8} />
                <Bar dataKey="news" fill="#F59E0B" opacity={0.8} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Posts Feed */}
      <Card className="glass-card border-gray-200 rounded-none" data-testid="dairy-posts-feed">
        <CardHeader className="pb-2 border-b border-gray-200 flex flex-row items-center justify-between">
          <CardTitle className="font-display text-sm font-semibold tracking-wider uppercase text-gray-800 flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: activePlatformColor }} />
            {platform === 'all' ? 'All Dairy Posts' : platform === 'reddit' ? 'Reddit Posts' : platform === 'tiktok' ? 'TikTok Videos' : 'News Alerts'}
            <Badge className="rounded-sm text-[9px] font-mono-data border-none px-1.5 py-0 bg-gray-100 text-gray-600">
              {total}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {loading ? (
            <div className="space-y-2 py-4">{[...Array(5)].map((_, i) => <div key={i} className="h-14 bg-gray-100 animate-pulse" />)}</div>
          ) : posts.length === 0 ? (
            <div className="py-16 text-center">
              <Clock className="w-8 h-8 text-gray-400 mx-auto mb-3" />
              <p className="font-mono-data text-sm text-gray-400">No dairy posts yet. Data is being fetched in the background.</p>
              <p className="font-mono-data text-xs text-gray-400 mt-1">Reddit & TikTok fetch every 6 hours. News alerts every hour.</p>
            </div>
          ) : (
            <>
              {posts.map((post, i) => (
                <DairyPostItem key={post.post_id || i} post={post} index={i} />
              ))}
              {pages > 1 && (
                <div className="flex items-center justify-center gap-4 pt-4 border-t border-gray-200">
                  <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}
                    className="px-3 py-1 text-xs font-display uppercase text-gray-500 hover:text-white disabled:opacity-30 border border-gray-200" data-testid="dairy-prev-page">Prev</button>
                  <span className="font-mono-data text-xs text-gray-500">{page} / {pages}</span>
                  <button disabled={page >= pages} onClick={() => setPage(p => p + 1)}
                    className="px-3 py-1 text-xs font-display uppercase text-gray-500 hover:text-white disabled:opacity-30 border border-gray-200" data-testid="dairy-next-page">Next</button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function DairySummaryCard({ summary, onRefresh, loading }) {
  return (
    <Card className="border-emerald-500/20 bg-gradient-to-br from-white to-emerald-50 rounded-none" data-testid="dairy-summary">
      <CardHeader className="pb-3 border-b border-emerald-200 flex flex-row items-center justify-between">
        <CardTitle className="font-display text-sm font-bold tracking-wider uppercase text-emerald-400 flex items-center gap-2">
          <Sparkles className="w-4 h-4" />
          Dairy Industry Intelligence Summary
        </CardTitle>
        <Button variant="ghost" size="sm" onClick={onRefresh} disabled={loading}
          className="rounded-none text-xs font-display uppercase tracking-wider text-gray-500 hover:text-emerald-400"
          data-testid="dairy-refresh-summary">
          <RefreshCw className={`w-3 h-3 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
          {loading ? 'GENERATING...' : 'REFRESH'}
        </Button>
      </CardHeader>
      <CardContent className="pt-4">
        {!summary ? (
          <div className="py-6 text-center">
            <Sparkles className="w-6 h-6 text-gray-400 mx-auto mb-2" />
            <p className="font-mono-data text-sm text-gray-400">AI summary will be generated once dairy data is collected.</p>
            <p className="font-mono-data text-xs text-gray-400 mt-1">Data is being fetched from Reddit, TikTok, and Google News.</p>
          </div>
        ) : (
          <div className="space-y-5">
            {summary.key_stat && (
              <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200">
                <TrendingUp className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                <span className="font-mono-data text-xs text-emerald-300">{summary.key_stat}</span>
              </div>
            )}

            {/* What's Being Discussed */}
            <div>
              <h3 className="font-display text-xs uppercase tracking-widest text-gray-500 mb-2 flex items-center gap-2">
                <MessageSquare className="w-3 h-3" /> What's Being Discussed
              </h3>
              <p className="font-mono-data text-sm text-gray-800 leading-relaxed">{summary.whats_discussed}</p>
            </div>

            {/* Content Opportunities */}
            <div>
              <h3 className="font-display text-xs uppercase tracking-widest text-gray-500 mb-2 flex items-center gap-2">
                <Lightbulb className="w-3 h-3" /> Content Marketing Opportunities
              </h3>
              {renderBullets(summary.content_opportunities)}
            </div>

            {/* Brand Entry */}
            <div>
              <h3 className="font-display text-xs uppercase tracking-widest text-gray-500 mb-2 flex items-center gap-2">
                <Target className="w-3 h-3" /> Brand Entry Opportunities
              </h3>
              {renderBullets(summary.brand_opportunities)}
            </div>

            <div className="flex items-center gap-3 pt-2 border-t border-gray-200">
              <span className="font-mono-data text-[10px] text-gray-400">
                Generated: {summary.generated_at ? new Date(summary.generated_at).toLocaleString('en-AU') : 'N/A'}
              </span>
              <span className="font-mono-data text-[10px] text-gray-400">
                Posts analyzed: {summary.total_posts_analyzed || 0}
              </span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function renderBullets(items) {
  if (!items) return null;
  if (typeof items === 'string') {
    return <p className="font-mono-data text-sm text-gray-600 leading-relaxed">{items}</p>;
  }
  if (Array.isArray(items)) {
    return (
      <ul className="space-y-1.5">
        {items.map((item, i) => (
          <li key={i} className="font-mono-data text-sm text-gray-600 leading-relaxed flex items-start gap-2">
            <span className="w-1 h-1 rounded-full bg-emerald-500 mt-2 flex-shrink-0" />
            {typeof item === 'string' ? item : item.text || item.description || JSON.stringify(item)}
          </li>
        ))}
      </ul>
    );
  }
  return null;
}

function DairyPostItem({ post, index }) {
  const themeColor = DAIRY_THEME_COLORS[post.theme] || '#6B7280';
  const sentimentColor = post.sentiment_label === 'positive' ? '#10B981' : post.sentiment_label === 'negative' ? '#EF4444' : '#6B7280';
  const platformLabel = post.platform;

  return (
    <div className="feed-item flex items-start gap-3 animate-fade-in-up" style={{ animationDelay: `${index * 15}ms` }} data-testid={`dairy-post-${index}`}>
      <div className="flex flex-col items-center flex-shrink-0 w-14 text-center pt-0.5">
        {platformLabel === 'reddit' ? (
          <>
            <ThumbsUp className="w-3 h-3 text-[#FF4500] mb-0.5" />
            <span className="font-mono-data text-xs font-bold text-gray-800">{post.upvotes || 0}</span>
          </>
        ) : platformLabel === 'tiktok' ? (
          <>
            <Eye className="w-3 h-3 text-[#00F2EA] mb-0.5" />
            <span className="font-mono-data text-xs font-bold text-gray-800">{fmtNum(post.views)}</span>
          </>
        ) : (
          <>
            <Globe className="w-3 h-3 text-[#F59E0B] mb-0.5" />
            <span className="font-mono-data text-[9px] font-bold text-gray-600">NEWS</span>
          </>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <a href={post.url} target="_blank" rel="noopener noreferrer" className="text-sm text-gray-800 hover:text-white transition-colors flex items-start gap-1.5">
          <span className="line-clamp-2">{post.title || 'Untitled'}</span>
          <ExternalLink className="w-3 h-3 flex-shrink-0 text-gray-400 mt-0.5" />
        </a>
        <div className="flex items-center gap-2 mt-1.5 flex-wrap">
          {platformLabel === 'reddit' && post.subreddit && (
            <span className="font-mono-data text-[10px] text-[#FF4500]">r/{post.subreddit}</span>
          )}
          {platformLabel === 'tiktok' && post.author && (
            <span className="font-mono-data text-[10px] text-[#00F2EA]">@{post.author}</span>
          )}
          {platformLabel === 'news' && post.source && (
            <span className="font-mono-data text-[10px] text-[#F59E0B]">{post.source}</span>
          )}
          <span className="font-mono-data text-[10px] text-gray-400 flex items-center gap-0.5">
            <MessageSquare className="w-2.5 h-2.5" /> {post.comment_count || 0}
          </span>
          {platformLabel === 'tiktok' && (
            <>
              <span className="font-mono-data text-[10px] text-gray-400 flex items-center gap-0.5">
                <ThumbsUp className="w-2.5 h-2.5" /> {fmtNum(post.likes)}
              </span>
              <span className="font-mono-data text-[10px] text-gray-400 flex items-center gap-0.5">
                <Share2 className="w-2.5 h-2.5" /> {fmtNum(post.shares)}
              </span>
            </>
          )}
          {/* Theme badge */}
          <span className="inline-flex items-center gap-1 px-1.5 py-0 text-[9px] font-mono-data" style={{ background: `${themeColor}15`, color: themeColor, border: `1px solid ${themeColor}25` }}>
            <span className="w-1.5 h-1.5 rounded-sm" style={{ background: themeColor }} />
            {DAIRY_THEME_NAMES[post.theme] || post.theme}
          </span>
          {/* Sentiment */}
          <span className="font-mono-data text-[9px] px-1.5 py-0" style={{ color: sentimentColor, background: `${sentimentColor}12`, border: `1px solid ${sentimentColor}20` }}>
            {post.sentiment_label}
            {post.sentiment_score != null && ` (${post.sentiment_score > 0 ? '+' : ''}${post.sentiment_score})`}
          </span>
          {/* Platform badge */}
          <span className={`font-mono-data text-[9px] px-1.5 py-0 ${
            platformLabel === 'reddit' ? 'text-[#FF4500] bg-[#FF4500]/10 border border-[#FF4500]/20' :
            platformLabel === 'tiktok' ? 'text-[#00F2EA] bg-[#00F2EA]/10 border border-[#00F2EA]/20' :
            'text-[#F59E0B] bg-[#F59E0B]/10 border border-[#F59E0B]/20'
          }`}>
            {platformLabel}
          </span>
          {/* Timestamp */}
          {post.created_utc > 0 && (
            <span className="font-mono-data text-[9px] text-gray-400">
              {new Date(post.created_utc * 1000).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, color, small }) {
  return (
    <Card className="bg-white border-gray-200 rounded-none shadow-sm">
      <CardContent className="py-3 px-4">
        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">{label}</p>
        <p className={`font-bold ${small ? 'text-xs' : 'text-lg'}`} style={{ color }}>{value}</p>
      </CardContent>
    </Card>
  );
}

function fmtNum(n) {
  if (!n) return '0';
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toString();
}
